import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Modal from "../components/Modal";
import Field from "../components/Field";
import Select from "../components/Select";

const PRIORITIES_LABELS = {
    LOW: "Faible",
    MEDIUM: "Moyenne",
    HIGH: "Importante",
};

const PRIORITIES_CLASSES = {
    LOW: "primary",
    MEDIUM: "warning",
    HIGH: "danger",
};

const STATUS_OPTIONS = [
    { value: "PENDING", label: "En attente" },
    { value: "IN_PROGRESS", label: "En cours" },
    { value: "SCHEDULED", label: "Programmé" },
    { value: "NOT_REQUIRED", label: "Non nécessaire" },
    { value: "RESOLVED", label: "Résolu" },
];

const STATUS_CLASSES = {
    PENDING: "secondary",
    IN_PROGRESS: "primary",
    SCHEDULED: "info",
    NOT_REQUIRED: "dark",
    RESOLVED: "success",
};

const ROLE_CLASSES = {
    ROLE_ADMIN: "primary",
    ROLE_MANAGER: "success",
    ROLE_FLEET_MANAGER: "info",
    ROLE_EMPLOYEE: "secondary"
};

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));



export default function TicketPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [priorities, setPriorities] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editPriority, setEditPriority] = useState("");
    const [editIsVisible, setEditIsVisible] = useState(true);

    const [editViolations, setEditViolations] = useState([]);

    const [showActivityModal, setShowActivityModal] = useState(false);
    const [activityStatus, setActivityStatus] = useState("PENDING");
    const [activityDescription, setActivityDescription] = useState("");
    const [activityViolations, setActivityViolations] = useState([]);

    const [activitySearch, setActivitySearch] = useState("");

    async function fetchTicketPriorities() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/ticket_priorities`);
            const data = await response.json();
            setPriorities(data);
        } catch (err) {
            // silently fail
        }
    }

    async function fetchTicket() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/tickets/${id}`);
            if (!response.ok) {
                setError("Ticket introuvable.");
                return;
            }
            const data = await response.json();
            setTicket(data);
        } catch (err) {
            setError("Erreur lors du chargement du ticket.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchTicket();
        fetchTicketPriorities();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    function openEditModal() {
        setEditPriority(ticket.priority ?? "");
        setEditIsVisible(ticket.isVisible ?? true);
        setEditViolations([]);
        setShowEditModal(true);
    }

    async function handleEditTicket() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/tickets/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    priority: editPriority,
                    isVisible: editIsVisible,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setEditViolations(errorData.violations ?? []);
                return;
            }
            setShowEditModal(false);
            setEditViolations([]);
            fetchTicket();
        } catch (err) {
            // silently fail
        }
    }

    async function handleCreateActivity() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/ticket_activities`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    status: activityStatus,
                    description: activityDescription,
                    ticket: `/api/tickets/${id}`,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setActivityViolations(errorData.violations ?? []);
                return;
            }
            setShowActivityModal(false);
            setActivityViolations([]);
            setActivityStatus("PENDING");
            setActivityDescription("");
            fetchTicket();
        } catch (err) {
            // silently fail
        }
    }

    if (loading) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-muted">Chargement...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !ticket) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-danger">{error ?? "Ticket introuvable."}</p>
                        <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => navigate("/tickets")}>
                            Retour aux tickets
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
        <Modal
            title="Modifier le ticket"
            body={
                <>
                    {editViolations.length > 0 && (
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="fas fa-times-circle me-3"></i>
                            <ul className="mb-0 list-unstyled">
                                {editViolations.map((v, i) => (
                                    <li key={i}>{v.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <Select
                        name="editPriority"
                        label="Priorité"
                        value={editPriority}
                        onChange={(e) => setEditPriority(e.target.value)}
                        options={priorities.map((p) => ({ value: p.value, label: p.label }))}
                    />
                    <div className="form-check mt-2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editIsVisible}
                            onChange={(e) => setEditIsVisible(e.target.checked)}
                            id="flexCheckEditVisible"
                        />
                        <label className="form-check-label" htmlFor="flexCheckEditVisible">Visible</label>
                    </div>
                </>
            }
            button={{ close: "Annuler", valid: "Modifier" }}
            showModal={showEditModal}
            setShowModal={setShowEditModal}
            onSubmit={handleEditTicket}
        />
        <Modal
            title="Ajouter une activité"
            body={
                <>
                    {activityViolations.length > 0 && (
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="fas fa-times-circle me-3"></i>
                            <ul className="mb-0 list-unstyled">
                                {activityViolations.map((v, i) => (
                                    <li key={i}>{v.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <Select
                        name="activityStatus"
                        label="Statut"
                        value={activityStatus}
                        onChange={(e) => setActivityStatus(e.target.value)}
                        options={STATUS_OPTIONS}
                    />
                    <Field
                        name="activityDescription"
                        label="Description"
                        value={activityDescription}
                        onChange={(e) => setActivityDescription(e.target.value)}
                        type="text"
                    />
                </>
            }
            button={{ close: "Annuler", valid: "Créer" }}
            showModal={showActivityModal}
            setShowModal={setShowActivityModal}
            onSubmit={handleCreateActivity}
        />
        <section>
            <div className="card mb-4">
                <div className="card-header py-3">
                    <div className="row py-3 align-items-center">
                        <div className="col-sm-6 text-center text-sm-start">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => navigate(-1)}
                            >
                                <i className="fas fa-arrow-left me-2"></i>
                                Retour
                            </button>
                        </div>
                        <div className="col-sm-6 text-center text-sm-end">
                            <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={openEditModal}
                            >
                                Éditer
                                <i className="fas fa-pen ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body py-5 px-5">
                    <div className="row g-4">
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Numéro de ticket</h6>
                            <p className="fw-bold mb-0">{ticket.ticketNumber}</p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem" }}>Créé le</h6>
                            <p className="mb-0">
                                {moment(ticket.createdAt).locale("fr").format("dddd Do MMMM YYYY [à] HH:mm")} par <span className={"badge badge-" + (ROLE_CLASSES[ticket.createdBy.role] ?? "secondary")}>
                                {ticket.createdBy.firstName} {ticket.createdBy.lastName}
                            </span>
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Véhicule</h6>
                            <p className="mb-0">
                                {ticket.vehicle?.licensePlateNumber}{" "}
                                <span className="text-muted">{ticket.vehicle?.name}</span>
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Kilométrage</h6>
                            <p className="mb-0">
                                {ticket.mileage != null ? `${ticket.mileage.toLocaleString("fr-FR")} km` : <span className="text-muted">—</span>}
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Priorité</h6>
                            <p className="mb-0">
                                <span className={"badge badge-" + (PRIORITIES_CLASSES[ticket.priority] ?? "secondary")}>
                                    {PRIORITIES_LABELS[ticket.priority] ?? ticket.priority}
                                </span>
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Statut</h6>
                            <p className="mb-0">{STATUS_LABELS[ticket.status] ?? <span className="text-muted">—</span>}</p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Visible</h6>
                            <p className="mb-0">
                                {ticket.isVisible ? (
                                    <i className="fa-solid fa-circle-check text-success"></i>
                                ) : (
                                    <i className="fa-solid fa-circle-xmark text-danger"></i>
                                )}
                            </p>
                        </div>
                        {ticket.description && (
                            <div className="col-12">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Description</h6>
                                <p className="mb-0">{ticket.description}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
        <section>
            <div className="card mb-4">
                <div className="card-header py-3">
                    <div className="row py-3 align-items-center">
                        <div className="col-sm-6 text-center text-sm-start">
                            <h5 className="mb-4 mb-sm-0 mt-2">Activités <span className="text-muted">({ticket.totalActivities})</span></h5>
                        </div>
                        <div className="col-sm-6 text-center text-sm-end">
                            <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={() => { setShowActivityModal(true); setActivityViolations([]); }}
                            >
                                Ajouter
                                <i className="fas fa-plus ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body py-5 px-5">
                    <div className="d-flex align-items-center mb-4">
                        <div className="flex-grow-1">
                            <Field
                                name="activitySearch"
                                label="Rechercher"
                                value={activitySearch}
                                onChange={(e) => setActivitySearch(e.target.value)}
                                type="text"
                                margin={false}
                            />
                        </div>
                    </div>
                    <table className="table table-hover card-body align-middle mb-0 bg-white">
                        <thead className="bg-light">
                            <tr>
                                <th className="text-left">Créé le</th>
                                <th className="text-center">Statut</th>
                                <th className="text-left">Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(ticket.ticketActivities ?? [])
                                .filter((a) =>
                                    a.description?.toLowerCase().includes(activitySearch.toLowerCase()) ||
                                    STATUS_LABELS[a.status]?.toLowerCase().includes(activitySearch.toLowerCase()) ||
                                    `${a.createdBy?.firstName} ${a.createdBy?.lastName}`.toLowerCase().includes(activitySearch.toLowerCase())
                                )
                                .map((activity) => (
                                    <tr key={activity.id}>
                                        <td className="text-left">
                                            {activity.createdAt
                                                ? moment(activity.createdAt).locale("fr").format("dddd Do MMMM YYYY [à] HH:mm")
                                                : <span className="text-muted">—</span>}
                                            {" "}par{" "}
                                            {activity.createdBy
                                                ? <span className={"badge badge-" + (ROLE_CLASSES[activity.createdBy.role] ?? "secondary")}>
                                                    {activity.createdBy.firstName} {activity.createdBy.lastName}
                                                  </span>
                                                : <span className="text-muted">—</span>}
                                        </td>
                                        <td className="text-center">
                                            <span className={"badge badge-" + (STATUS_CLASSES[activity.status] ?? "secondary")}>
                                                {STATUS_LABELS[activity.status] ?? activity.status}
                                            </span>
                                        </td>
                                        <td>{activity.description ?? <span className="text-muted">—</span>}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
        </>
    );
}
